import { useState } from "react";
import { useBookingFlow } from "../../session/BookingFlowProvider";
import { Field, Panel, Stepper } from "../../components/ui";
import { DateRangePicker } from "../../components/DateRangePicker";
import { reasonsForStep } from "../FlowMachine";
import type { MinorGuestInput } from "../../bridge/contract";

export function StayStep() {
  const { projection, send, bootstrap } = useBookingFlow();
  const reasons = reasonsForStep(projection, "stay");
  const stay = projection.stay;

  // Shown only once the guest has actually picked something. "Please choose your dates" under an
  // untouched calendar is not help, it is a reprimand for opening the widget.
  const [touched, setTouched] = useState(Boolean(stay));
  const lengthReason = reasons.find((reason) => reason.field === "checkout");

  return (
    <Panel title="Ihr Reisezeitraum">
      <DateRangePicker
        value={{ checkin: stay?.checkin, checkout: stay?.checkout }}
        maxNights={bootstrap.rules.maxNights}
        onChange={({ checkin, checkout }) => {
          setTouched(true);
          if (checkin === stay?.checkin && checkout === stay?.checkout) return;
          void send({ type: "set_stay", checkin, checkout });
        }}
      />
      {touched && lengthReason ? (
        <p className="field-error" role="alert">
          {lengthReason.message}
        </p>
      ) : null}
    </Panel>
  );
}

export function RoomsStep() {
  const { projection, send, bootstrap, pending } = useBookingFlow();
  const reasons = reasonsForStep(projection, "rooms");
  const canAdd = projection.rooms.length < bootstrap.rules.maxRooms;

  return (
    <Panel title="Wie viele Zimmer brauchen Sie?" description="Jedes Zimmer wird einzeln belegt und bepreist.">
      <ul className="room-list">
        {projection.rooms.map((room, index) => (
          <li key={room.roomId}>
            <span>Zimmer {index + 1}</span>
            {projection.rooms.length > 1 ? (
              <button
                type="button"
                className="link-button"
                disabled={pending}
                onClick={() => void send({ type: "remove_room", roomId: room.roomId })}
              >
                Entfernen
              </button>
            ) : null}
          </li>
        ))}
      </ul>
      <button
        type="button"
        className="button button--ghost"
        disabled={!canAdd || pending}
        onClick={() => void send({ type: "add_room" })}
      >
        Weiteres Zimmer hinzufügen
      </button>
      {reasons.map((reason) => (
        <p key={reason.code} className="field-error" role="alert">
          {reason.message}
        </p>
      ))}
    </Panel>
  );
}

export function PartyStep() {
  const { projection, send, bootstrap, pending } = useBookingFlow();
  const { parentBedMaxAge, requiresMinorAges, maxAdultsPerRoom } = bootstrap.rules;

  function pushParty(roomId: string, adults: number, minors: MinorGuestInput[]) {
    void send({ type: "update_room_party", roomId, adults, minors });
  }

  return (
    <Panel title="Wer reist mit?" description="Danach richten sich Kapazität und Preis.">
      {projection.rooms.map((room, index) => {
        const reasons = reasonsForStep(projection, "party", room.roomId);
        const minors: MinorGuestInput[] = room.minors.map((m) => ({
          age: m.age,
          sleepsInParentBed: m.sleepsInParentBed,
        }));
        return (
          <fieldset key={room.roomId} className="room-block">
            <legend>Zimmer {index + 1}</legend>

            <div className="row-between">
              <span>Erwachsene</span>
              <Stepper
                label="Erwachsene"
                value={room.adults}
                min={1}
                max={maxAdultsPerRoom}
                onChange={(next) => pushParty(room.roomId, next, minors)}
              />
            </div>

            <div className="row-between">
              <span>Kinder</span>
              <Stepper
                label="Kinder"
                value={minors.length}
                min={0}
                max={6}
                onChange={(next) => {
                  const resized =
                    next > minors.length
                      ? [...minors, { age: null, sleepsInParentBed: false }]
                      : minors.slice(0, next);
                  pushParty(room.roomId, room.adults, resized);
                }}
              />
            </div>

            {room.minors.map((minor, minorIndex) => {
              // The parent-bed question only appears where the hotel published an age limit for
              // it and the stated age is inside that limit. Everyone older needs a bed, so
              // asking would only invite a wrong answer.
              const eligibleForParentBed =
                parentBedMaxAge !== null && minor.age !== null && minor.age <= parentBedMaxAge;
              return (
                <div className="minor-row" key={minor.id}>
                  <Field
                    label={`Alter Kind ${minorIndex + 1}`}
                    htmlFor={`${minor.id}-age`}
                    hint={requiresMinorAges ? "Bei Anreise" : undefined}
                    reason={reasons.find((r) => r.field === "minorAge" && minor.age === null)}
                  >
                    <input
                      id={`${minor.id}-age`}
                      type="number"
                      min={0}
                      max={17}
                      value={minor.age ?? ""}
                      disabled={pending}
                      onChange={(event) => {
                        const parsed = event.target.value === "" ? null : Number(event.target.value);
                        const next = minors.map((m, i) => (i === minorIndex ? { ...m, age: parsed } : m));
                        pushParty(room.roomId, room.adults, next);
                      }}
                    />
                  </Field>

                  {eligibleForParentBed ? (
                    <label className="inline-check" htmlFor={`${minor.id}-parentbed`}>
                      <input
                        id={`${minor.id}-parentbed`}
                        type="checkbox"
                        checked={minor.sleepsInParentBed}
                        onChange={(event) => {
                          const next = minors.map((m, i) =>
                            i === minorIndex ? { ...m, sleepsInParentBed: event.target.checked } : m,
                          );
                          pushParty(room.roomId, room.adults, next);
                        }}
                      />
                      <span>Schläft im Elternbett</span>
                    </label>
                  ) : null}

                  {minor.age !== null && !minor.countsAgainstCapacity ? (
                    <p className="field-hint">Zählt nicht gegen die Zimmerkapazität.</p>
                  ) : null}
                </div>
              );
            })}

            {reasons
              .filter((reason) => reason.field !== "minorAge")
              .map((reason) => (
                <p key={reason.code} className="field-error" role="alert">
                  {reason.message}
                </p>
              ))}
          </fieldset>
        );
      })}
    </Panel>
  );
}
